/*
 * NAO-97 T4: the sticky bar and the peek drawer (snippets/pdp-bar.liquid, BUILD-PLAN 1.7).
 * Contract: top of assets/naomi-pdp.js.
 *
 * Bar (tablets and desktop): shown once the buy box button [data-pdp-cta] has scrolled
 * above the screen (or while it is hidden), under the site header when that is revealed.
 * Mirrors the buy box: [data-pdp-config], the button label and state, and the price from
 * #price-<section> into every button's [data-pdp-cta-price] (the buy box's too). Chapter
 * links come from [data-pdp-chapter].
 *
 * Peek (phones, the only bar there, so nothing sticks to the top over the site header):
 * drawn over the buy box's top rows while the buy box is still below them, so it is on
 * through the photo stack and steps aside the frame the buy box top passes it, at any
 * scroll speed; its dots follow the photo in the middle of the screen, and a tap on the
 * rail scrolls to the nearest dot's photo. Past the buy box button it slides back as the
 * compact bar (one row: configuration and arrival, a button sized to its label; the
 * chapter links under it) until the footer. A tap on its top area, or a drag up from it,
 * brings the real buy box up in its place (liftBuyBox, below).
 *
 * Choosing first: the "Select options" gate (the buy box comes up until an option is
 * touched) is off since the owner's 2026-09-27 call, see connectedCallback. Jump targets take focus with no scroll and no ring
 * (pdp-gallery.css, [tabindex='-1']:focus).
 */
if (!customElements.get('pdp-bar')) {
  // the iOS sheet curve, cubic-bezier(0.32, 0.72, 0, 1), for every drawer motion
  // (pdp-gallery.css --pdp-sheet-ease matches it): x(t) solved by Newton steps, then y(t)
  const sheetEase = (x) => {
    const bez = (t, a, b) => 3 * a * t * (1 - t) ** 2 + 3 * b * t * t * (1 - t) + t ** 3;
    const slope = (t, a, b) => 3 * a * (1 - t) ** 2 + 6 * (b - a) * t * (1 - t) + 3 * (1 - b) * t * t;
    let t = x;
    for (let i = 0; i < 8; i++) {
      t = Math.min(1, Math.max(0, t - (bez(t, 0.32, 0) - x) / (slope(t, 0.32, 0) || 1e-6)));
    }
    return bez(t, 0.72, 1);
  };

  customElements.define(
    'pdp-bar',
    class PdpBar extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.peek = document.querySelector('[data-pdp-peek]');
        this.title = this.querySelector('[data-pdp-bar-cfg]')?.textContent.trim() || '';
        // ponytail: gate off (owner 2026-09-27): every option starts on a real default and a
        // missing ring size gates itself ("Select a ring size"); set back to
        // `this.dataset.hasOptions !== 'true'` for "Select options" until a choice
        this.chosen = true;
        this.shown = {};

        this.buildChapters();
        this.sync();
        this.update();

        this.mutationObserver = new MutationObserver((mutations) => {
          if (mutations.every((m) => [this, this.peek].some((el) => el?.contains(m.target)))) return;
          this.schedule('sync');
        });
        if (this.productInfo) {
          this.mutationObserver.observe(this.productInfo, {
            subtree: true,
            childList: true,
            attributes: true,
            characterData: true,
          });
        }

        this.onScroll = () => this.schedule('update');
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onClick = this.handleClick.bind(this);
        document.addEventListener('click', this.onClick);
        this.onChoice = this.handleChoice.bind(this);
        document.addEventListener('change', this.onChoice, true);
        document.addEventListener('click', this.onChoice, true);
        document.addEventListener('nj:sizer', this.onChoice);
        this.onMedia = () => this.schedule('sync');
        this.mobile.addEventListener('change', this.onMedia);
        if (this.peek) this.bindDrag(this.peek.querySelector('[data-pdp-peek-open]'));
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.removeEventListener('click', this.onClick);
        document.removeEventListener('change', this.onChoice, true);
        document.removeEventListener('click', this.onChoice, true);
        document.removeEventListener('nj:sizer', this.onChoice);
        this.mobile.removeEventListener('change', this.onMedia);
      }

      // any option touched in the buy box counts as a choice, the already-selected chip too
      handleChoice(event) {
        if (this.chosen) return;
        if (event.type === 'nj:sizer') {
          if (!event.detail?.on) return;
        } else if (!event.target.closest?.('[data-pdp-buy] variant-selects :is(input, select)')) {
          return;
        }
        this.chosen = true;
        this.schedule('sync');
      }

      track(event, props) {
        window.NaomiPDP?.track(event, props);
      }

      schedule(what) {
        this.pending = this.pending || new Set();
        this.pending.add(what);
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const todo = this.pending;
          this.pending = null;
          if (todo.has('sync')) this.sync();
          this.update();
        });
      }

      buyRoot() {
        return document.querySelector('[data-pdp-buy]');
      }

      navs() {
        return document.querySelectorAll('[data-pdp-bar-chapters]');
      }

      // Chapters with no content never render, so their links never exist.
      // Both bars lead with Overview, back to the buy box (marked while no chapter is); the
      // buy box's own index skips it (owner 2026-09-27: desktop shows Overview too).
      buildChapters() {
        this.chapters = Array.from(document.querySelectorAll('[data-pdp-chapter]'));
        const link = (href, chapter, label) => {
          const a = document.createElement('a');
          a.href = href;
          a.dataset.chapter = chapter;
          a.textContent = label || '';
          return a;
        };
        this.navs().forEach((nav) => {
          const links = this.chapters.map((c) => link(`#${c.id}`, c.dataset.pdpChapter, c.dataset.label));
          if (this.chapters.length) {
            links.unshift(link(`#${this.buyRoot()?.id}`, 'overview', this.dataset.overviewLabel));
          }
          nav.replaceChildren(...links);
        });
      }

      // One source for every line: the buy box.
      sync() {
        const buy = this.buyRoot();
        const cta = buy?.querySelector('[data-pdp-cta]');
        const config = buy?.querySelector('[data-pdp-config]')?.textContent.trim() || '';
        const price = document.getElementById(`price-${this.dataset.sectionId}`);
        const priceItem = price?.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular');
        const priceText = price && !price.classList.contains('hidden') ? priceItem?.textContent.trim() || '' : '';
        // ponytail: the buttons drop the currency code the buy box price carries ("$1,465 USD")
        const buttonPrice = priceText.replace(/\s+[A-Z]{3}$/, '');
        // the label span when there is one (buy-buttons), else the button's own text
        const label = (cta?.querySelector('span:not(.visually-hidden)') || cta)?.textContent.replace(/\s+/g, ' ').trim();
        const disabled = !!cta?.hasAttribute('disabled');
        const gated = this.mobile.matches && !this.chosen && !disabled;

        // a line whose text changed dissolves in (NaomiPDP.fade: after an option pick only)
        const set = (el, text) => {
          if (!el || el.textContent === text) return;
          el.textContent = text;
          window.NaomiPDP?.fade(el);
        };
        this.querySelectorAll('[data-pdp-bar-cfg]').forEach((el) => set(el, (!gated && config) || this.title));
        const peekCfg = this.peek?.querySelector('[data-pdp-bar-cfg]');
        if (peekCfg) {
          // each part stays whole with its dot, so a wrap (compact bar) falls only between parts
          if (peekCfg.dataset.text !== config) {
            peekCfg.dataset.text = config;
            const parts = config ? config.split(' · ') : [];
            peekCfg.replaceChildren(
              ...parts.flatMap((part, i) => {
                const span = document.createElement('span');
                span.className = 'pdp-peek__part';
                span.textContent = i < parts.length - 1 ? `${part} ·` : part;
                return i < parts.length - 1 ? [span, ' '] : [span];
              })
            );
            window.NaomiPDP?.fade(peekCfg);
          }
          peekCfg.hidden = gated || !config;
          // the compact bar shows the configuration in place of the title (pdp-gallery.css)
          this.peek.classList.toggle('has-cfg', !peekCfg.hidden);
        }
        document.querySelectorAll('[data-pdp-cta-price]').forEach((el) => set(el, buttonPrice));
        document.querySelectorAll('[data-pdp-bar-cta]').forEach((button) => {
          if (label) set(button.querySelector('[data-pdp-bar-label]'), gated ? this.dataset.selectLabel : label);
          if (cta) button.toggleAttribute('disabled', disabled);
        });
        this.buildDots();
      }

      // ---------- the peek (C) ----------
      // the photos on show: a colour change hides the other colours' frames
      photos() {
        return Array.from(document.querySelectorAll('.pdp-gallery__stack > .product__media-item')).filter(
          (item) => item.offsetParent !== null
        );
      }

      // one dot per photo; a colour change swaps the photos, so this runs on every sync
      buildDots() {
        const list = this.peek?.querySelector('[data-pdp-peek-dots]');
        if (!list) return;
        const count = this.photos().length;
        const wanted = count > 1 ? count : 0;
        if (list.childElementCount === wanted) return;
        list.replaceChildren(...Array.from({ length: wanted }, () => document.createElement('li')));
        this.dot = -1;
      }

      // the current photo is the last one whose top has passed the middle of the photo area
      updateDots() {
        const dots = this.peek.querySelector('[data-pdp-peek-dots]')?.children;
        if (!dots?.length) return;
        const middle = (window.innerHeight - this.peek.offsetHeight) / 2;
        let current = 0;
        this.photos().forEach((page, i) => {
          if (page.getBoundingClientRect().top <= middle) current = i;
        });
        if (current === this.dot) return;
        dots[this.dot]?.removeAttribute('data-current');
        dots[current]?.setAttribute('data-current', '');
        this.dot = current;
      }

      glide(top) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: reduce ? 'auto' : 'smooth' });
      }

      // glide to a target and hand it focus with no scroll (no ring: pdp-gallery.css). Going
      // down hides the header; on the way up it comes back, so stop under it, and under the
      // bar when it stays on there (chapters: yes, the buy box: no).
      jump(target, underBar = true) {
        if (target === this.buyRoot()) {
          // the buy box lands where the drawer lands it (landing, below)
          const down = this.landing(false);
          this.glide(down < window.scrollY ? this.landing(true) : down);
        } else {
          const top = target.getBoundingClientRect().top + window.scrollY;
          const header = document.querySelector('.section-header');
          const up = top < window.scrollY;
          // the bar sits under a revealed header (pdp-gallery.css), so the two stack
          const cover = (up && header ? header.offsetHeight : 0) + (underBar ? this.cover || 0 : 0);
          this.glide(top - cover);
        }
        if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }

      // the buy box up: over the photos the drawer rises as a sheet (below), elsewhere the
      // page glides to the buy box
      rise(reason, surface = 'peek') {
        const buy = this.buyRoot();
        if (!buy) return;
        const peek = this.peek;
        if (surface === 'peek' && peek && !peek.classList.contains('is-late') && !peek.classList.contains('is-away')) {
          return this.openSheet(reason);
        }
        this.jump(buy, false);
        this.track('sheet_expanded', { reason, surface });
      }

      // ---------- the photo drawer drives the real buy box (owner 2026-09-27) ----------
      // The drawer's handle and title are drawn exactly over the buy box's own. A pull or a tap
      // first lifts the real buy box so its top rows sit exactly under the drawer's: the photo
      // column is cut short at the drawer top (the photos under the drawer are hidden anyway), and
      // the white band over the buy box handle (pdp-gallery.css) starts tucked under the last
      // photo, uncovered as the page moves. The drawer fades in 120ms while riding with the page,
      // and from there the document itself scrolls: 1:1 under the finger in rAF, rubber-banding
      // past either end, then one glide on the sheet curve that carries the release speed. The
      // price, the options and the button come up the whole way and nothing is swapped at the
      // end: once up, the photos under the drawer stay out until the shopper is back in the
      // photos (settleBuyBox, keepHeld).
      reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // Where the buy box lands, the one offset for every path (owner 2026-09-27): 2.4rem of
      // white above its handle line (above its title where no handle shows), under the site
      // header when it shows (on the way up), else under the safe area. Returns the scroll top.
      landing(headerShows) {
        const buy = this.buyRoot();
        const handle = buy.querySelector(':scope > .pdp-peek__handle');
        // phones: the sheet's top edge (its handle) pins under the header; else 2.4rem over the title
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
        const mark = handle?.offsetHeight
          ? handle.getBoundingClientRect().top
          : (buy.querySelector('.product__title') || buy).getBoundingClientRect().top - 2.4 * rem;
        const header = document.querySelector('.section-header');
        // ponytail: the safe area is read off the drawer's scroll-margin-top (pdp-gallery.css)
        const base = headerShows && header ? header.offsetHeight : parseFloat(getComputedStyle(this.peek || buy).scrollMarginTop) || 0;
        return window.scrollY + mark - base;
      }

      // every drawer scroll goes through here: the drawer rides with the page while it fades,
      // the band over the buy box uncovers with the rise, and the site header stays hidden when
      // the page moves back down (rubber band, a return to rest)
      scrollPage(y) {
        const top = Math.max(0, Math.round(y));
        const lift = this.lift;
        if (lift && top < window.scrollY) document.querySelector('sticky-header')?.dispatchEvent(new CustomEvent('preventHeaderReveal'));
        window.scrollTo({ top, behavior: 'instant' });
        if (!lift) return;
        const moved = window.scrollY - lift.start;
        this.peek.style.transform = `translate3d(0, ${-moved}px, 0)`;
        const p = Math.min(1, Math.max(0, moved / lift.travel));
        lift.stack.style.clipPath = `inset(0 0 ${(lift.below + lift.overlap * p).toFixed(2)}px)`;
      }

      // To y on the sheet curve in rAF (the browser's smooth scroll has its own curve). v, the
      // page's speed at release in px/ms: the curve leaves at a slope of 2.25 (0.72 / 0.32), so
      // 2.25 * distance / v carries it with no step at the handoff. Otherwise shorter when there
      // is little to go; 200 to 520ms. Reduced motion lands at once.
      sheetGlide(y, v, done) {
        cancelAnimationFrame(this.glideFrame);
        this.glideFrame = null;
        const from = window.scrollY;
        const dist = Math.abs(y - from);
        if (this.reduceMotion() || dist < 1) {
          this.scrollPage(y);
          return done();
        }
        let ms = Math.min(480, 220 + dist * 0.25);
        // ponytail: one curve cannot match a slow release inside 520ms; a spring would
        if (v && Math.sign(v) === Math.sign(y - from)) ms = Math.min(520, (2.25 * dist) / Math.abs(v));
        ms = Math.max(200, ms);
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min(1, Math.max(0, (now - t0) / ms));
          this.scrollPage(from + (y - from) * sheetEase(p));
          if (p < 1) this.glideFrame = requestAnimationFrame(step);
          else {
            this.glideFrame = null;
            done();
          }
        };
        this.glideFrame = requestAnimationFrame(step);
      }

      liftBuyBox() {
        this.release();
        const buy = this.buyRoot();
        const col = buy?.closest('.product__info-wrapper');
        const media = col?.previousElementSibling;
        const peekHandle = this.peek.querySelector('.pdp-peek__handle');
        const buyHandle = buy?.querySelector(':scope > .pdp-peek__handle');
        if (!media || !peekHandle?.offsetHeight || !buyHandle?.offsetHeight) return null;
        const start = window.scrollY;
        const drawerTop = this.peek.getBoundingClientRect().top;
        // how far the buy box handle is under the drawer's, and the part of it that is the
        // photos under the drawer (cut); the rest is the band, laid over the last photo
        const gap = buyHandle.getBoundingClientRect().top - peekHandle.getBoundingClientRect().top;
        const cut = Math.max(0, Math.min(gap, media.getBoundingClientRect().bottom - drawerTop));
        if (gap < 0) return null;
        const height = media.getBoundingClientRect().height;
        const margin = parseFloat(getComputedStyle(col).marginTop) || 0;
        media.style.height = `${height - cut}px`;
        col.style.marginTop = `${margin - (gap - cut)}px`;
        // settle it on the drawer to the pixel (subpixel layout), and undo any scroll anchoring
        const off = buyHandle.getBoundingClientRect().top - peekHandle.getBoundingClientRect().top;
        if (Math.abs(off) >= 0.5) col.style.marginTop = `${margin - (gap - cut) - off}px`;
        if (window.scrollY !== start) window.scrollTo({ top: start, behavior: 'instant' });
        this.sheetBusy = true;
        this.peek.classList.add('is-handing');
        const overlap = margin - parseFloat(col.style.marginTop);
        // the photos paint past the shortened column (they bleed to the screen edges, so no
        // overflow clip on it): clip them there instead, below the drawer top
        const stack = media.querySelector('.pdp-gallery__stack') || media;
        const below = stack.getBoundingClientRect().bottom - drawerTop;
        stack.style.clipPath = `inset(0 0 ${below}px)`;
        this.lift = { col, media, stack, below, overlap, start, travel: 0 };
        // the buy box lands where every path lands it (going down hides the header)
        this.lift.travel = Math.max(1, this.landing(false) - start);
        return this.lift;
      }

      // Opened: the cut is held (owner 2026-09-29), so scrolling back up meets the photo the
      // shopper left, not the last one: the stack itself is cut to what shows (its own height,
      // clipped, so a hidden frame that loads at another size moves nothing), the column settled
      // back on the buy box, in one frame, so nothing moves. keepHeld (update) gives the photos
      // back. Back at rest: the drawer fades back in over the buy box top, then the lift goes.
      settleBuyBox(opened) {
        const { col, media, stack, below, overlap, start, travel } = this.lift;
        const buy = this.buyRoot();
        const peek = this.peek;
        this.lift = null;
        const drop = () => {
          this.settleTimer = clearTimeout(this.settleTimer);
          col.style.marginTop = '';
          media.style.height = '';
          stack.style.clipPath = '';
          this.sheetBusy = false;
        };
        if (opened) {
          const was = buy.getBoundingClientRect().top;
          // the part of the stack scrollPage's clip hides now
          const hidden = below + overlap * Math.min(1, Math.max(0, (window.scrollY - start) / travel));
          const s = stack.getBoundingClientRect();
          // ponytail: media-gallery.js scrolls by a stack resize whose old bottom is above the
          // screen, so a cut hiding no more than that (a sliver of the last photo) drops as before
          if (hidden >= 1 && s.bottom > 0) {
            media.style.height = '';
            stack.style.height = `${s.height - hidden}px`;
            stack.style.clipPath = 'inset(0)';
            col.style.marginTop = `${parseFloat(col.style.marginTop) - (buy.getBoundingClientRect().top - was)}px`;
            this.sheetBusy = false;
            // floor: the drawer top (keepHeld)
            const floor = window.innerHeight - peek.getBoundingClientRect().height;
            this.held = { col, stack, shown: this.photos(), width: window.innerWidth, floor };
          } else {
            drop();
            window.scrollTo({ top: window.scrollY + buy.getBoundingClientRect().top - was, behavior: 'instant' });
          }
          if (!buy.hasAttribute('tabindex')) buy.tabIndex = -1;
          buy.focus({ preventScroll: true });
          // straight to the state the page is now in (away), transitions back two frames later
          peek.style.transition = 'none';
          peek.style.transform = '';
          peek.classList.remove('is-handing');
          this.update();
          void peek.offsetHeight;
          requestAnimationFrame(() => requestAnimationFrame(() => (peek.style.transition = '')));
          return;
        }
        peek.style.transform = '';
        peek.style.transition = 'opacity 120ms linear';
        peek.classList.remove('is-handing');
        this.settleDrop = () => {
          drop();
          peek.style.transition = '';
          this.update();
        };
        this.settleTimer = setTimeout(this.settleDrop, 140);
      }

      openSheet(reason, v = 0) {
        if (this.sheetBusy) return;
        const buy = this.buyRoot();
        this.track('sheet_expanded', { reason, surface: 'peek' });
        // reduced motion: a short fade in place of the rise (the drawer fades out, pdp-gallery.css)
        if (this.reduceMotion() || !this.liftBuyBox()) {
          if (this.reduceMotion()) buy.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150 });
          return this.jump(buy, false);
        }
        this.sheetGlide(this.lift.start + this.lift.travel, v, () => this.settleBuyBox(true));
      }

      // The held cut (settleBuyBox) goes once its end is under the drawer top, the drawer being
      // back by then: the photos return below it and only the buy box moves, out of sight. Also
      // on a turn of the phone, and at the page top (a cut that never reaches the drawer top). A
      // colour change swaps the frames: the way back up starts at the new first photo, the cut
      // no higher than the drawer top at the page top, so it still goes (media-gallery.js
      // scrolls by the resize while the cut is above the screen, so the buy box stays put).
      keepHeld(mobile, vh) {
        const { stack, shown, width, floor } = this.held;
        if (!mobile || window.innerWidth !== width || window.scrollY <= 0 || stack.getBoundingClientRect().bottom >= vh - this.peek.getBoundingClientRect().height) {
          return this.release();
        }
        const now = this.photos();
        if (now.length === shown.length && now.every((photo, i) => photo === shown[i])) return;
        this.held.shown = now;
        if (!now[0]) return this.release();
        const top = stack.getBoundingClientRect().top;
        stack.style.height = `${Math.max(now[0].getBoundingClientRect().bottom, floor - window.scrollY) - top}px`;
      }

      // the whole stack back (a new lift and a dot tap first, as they measure the photos)
      release() {
        if (!this.held) return;
        const { col, stack } = this.held;
        this.held = null;
        stack.style.height = '';
        stack.style.clipPath = '';
        col.style.marginTop = '';
      }

      // The drawer's top area (touch-action: none, pdp-gallery.css, so Safari hands every
      // gesture there to this, momentum or not). Its first touch takes the drag at once: up
      // lifts the buy box, down moves the page with the finger. While a lift is under way the
      // buy box itself is the grab: a touch stops the glide where it is and the finger carries
      // on from there. Nothing waits for the page to settle. The late bar has no drag.
      bindDrag(zone) {
        const col = this.buyRoot()?.closest('.product__info-wrapper');
        if (!zone || !col) return;
        const peek = this.peek;
        let d = null;
        const rubber = (over) => (over * 0.35) / (1 + over / 500);
        const frame = () => {
          this.dragFrame = null;
          if (d && d.up !== null) this.scrollPage(d.top);
        };
        const start = (e) => {
          d = null;
          if (e.touches.length !== 1) return;
          const y = e.touches[0].clientY;
          const sample = [[e.timeStamp, y]];
          if (this.lift) {
            // a touch while the buy box is lifted: only while it glides (a drag already owns it)
            if (!this.glideFrame) return;
            cancelAnimationFrame(this.glideFrame);
            this.glideFrame = null;
            d = { y0: y, up: true, base: window.scrollY, lift: this.lift, top: window.scrollY, samples: sample };
            return;
          }
          if (e.currentTarget !== zone || peek.classList.contains('is-late') || peek.classList.contains('is-away')) return;
          // the drawer is fading back in over a return to rest: finish that now
          if (this.settleTimer) this.settleDrop();
          d = { y0: y, up: null, base: window.scrollY, lift: null, top: window.scrollY, samples: sample };
        };
        const move = (e) => {
          if (!d) return;
          const cy = e.touches[0].clientY;
          const dy = cy - d.y0;
          if (d.up === null) {
            if (dy === 0) return;
            d.up = dy < 0;
            if (d.up && !(d.lift = this.liftBuyBox())) return (d = null);
          }
          if (e.cancelable) e.preventDefault();
          const raw = d.base - dy;
          if (d.lift) {
            const lo = d.lift.start;
            const hi = lo + d.lift.travel;
            d.top = raw < lo ? lo - rubber(lo - raw) : raw > hi ? hi + rubber(raw - hi) : raw;
          } else d.top = raw;
          d.samples.push([e.timeStamp, cy]);
          if (d.samples.length > 6) d.samples.shift();
          if (!this.dragFrame) this.dragFrame = requestAnimationFrame(frame);
        };
        const end = () => {
          const g = d;
          d = null;
          if (!g || g.up === null) return;
          cancelAnimationFrame(this.dragFrame);
          this.dragFrame = null;
          this.scrollPage(g.top);
          this.dragged = Date.now();
          if (!g.lift) return;
          // the finger's speed over its last 100ms (px/ms, negative is up)
          const last = g.samples[g.samples.length - 1];
          const first = g.samples.find((sample) => last[0] - sample[0] <= 100) || last;
          const v = last[0] > first[0] ? (last[1] - first[1]) / (last[0] - first[0]) : 0;
          const { start: lo, travel } = g.lift;
          // a flick wins either way; a slow pull opens past 40% of the way
          const open = v < -0.5 || (v <= 0.5 && window.scrollY - lo > 0.4 * travel);
          if (open) this.track('sheet_expanded', { reason: 'drag', surface: 'peek' });
          // the page moves against the finger, so its speed is -v
          this.sheetGlide(open ? lo + travel : lo, -v, () => this.settleBuyBox(open));
        };
        [zone, col].forEach((el) => {
          el.addEventListener('touchstart', start, { passive: true });
          el.addEventListener('touchmove', move, { passive: false });
          el.addEventListener('touchend', end);
          el.addEventListener('touchcancel', end);
        });
      }

      // a tap anywhere on the rail (it is padded well past the dots) takes the nearest dot
      jumpTo(clientY) {
        this.release();
        const dots = Array.from(this.peek.querySelector('[data-pdp-peek-dots]')?.children || []);
        if (!dots.length) return;
        let index = 0;
        let best = Infinity;
        dots.forEach((dot, i) => {
          const r = dot.getBoundingClientRect();
          const d = Math.abs(r.top + r.height / 2 - clientY);
          if (d < best) [index, best] = [i, d];
        });
        const photo = this.photos()[index];
        if (!photo) return;
        const top = photo.getBoundingClientRect().top + window.scrollY;
        // the header comes back on the way up and would cover the photo's top: stop under it
        const header = document.querySelector('.section-header');
        const up = top < window.scrollY;
        this.glide(index === 0 ? 0 : up && header ? top - header.offsetHeight : top);
        this.track('gallery_dot_clicked', { index: index + 1, total: dots.length });
      }

      update() {
        const mobile = this.mobile.matches;
        const buy = this.buyRoot();
        const cta = buy?.querySelector('[data-pdp-cta]');
        const rect = cta?.getBoundingClientRect();
        // hidden inline button (bar_only, or display none): nothing to wait for
        const ctaHidden = !rect || (rect.width === 0 && rect.height === 0);
        const pastCta = ctaHidden || rect.bottom < 0;
        const vh = window.innerHeight;
        if (this.held) this.keepHeld(mobile, vh);

        // Phones: the peek is the only bar. Photo side (the buy box still in the lower half):
        // on while the buy box top is at or under the peek top (1px of slack), drawn over the
        // buy box's top rows, so it steps aside with no motion. Late side: the compact bar
        // slides up once the buy box button has left the screen, and down over the footer. Late side (the buy box higher up): the compact peek with the
        // chapter links slides up once the buy box button has left the screen, and steps
        // aside over the footer.
        let peekOn = false;
        if (this.sheetBusy) {
          // a pull or a tap owns the drawer until the buy box settles (settleBuyBox runs this again)
          peekOn = !this.peek.classList.contains('is-away');
        } else if (mobile && this.peek) {
          const buyTop = buy?.getBoundingClientRect().top;
          const photoSide = buyTop !== undefined && buyTop > vh / 2;
          this.peek.classList.toggle('is-late', !photoSide);
          if (photoSide) {
            // the buy box handle sits the band lower in it than the drawer's (pdp-gallery.css)
            const band = (buy.querySelector(':scope > .pdp-peek__handle')?.offsetTop || 0) - (this.peek.querySelector('.pdp-peek__handle')?.offsetTop || 0);
            peekOn = buyTop + band >= vh - this.peek.offsetHeight - 1;
          } else {
            const footer = document.querySelector('.footer') || document.getElementById('shopify-section-footer');
            peekOn = pastCta && !(footer && footer.getBoundingClientRect().top < vh);
          }
          this.peek.classList.toggle('is-away', !peekOn);
          this.peek.classList.toggle('is-low', !peekOn && !photoSide);
          this.peek.toggleAttribute('inert', !peekOn);
          if (peekOn && photoSide) this.updateDots();
          if (peekOn && !this.shown.peek) {
            this.shown.peek = true;
            this.track('bar_shown', { surface: 'peek' });
          }
        }

        // Tablets and desktop: the top bar with the chapter links, past the buy box button.
        // Phones never show it (pdp-gallery.css), so the site header stays reachable.
        const barOn = !mobile && pastCta;
        this.classList.toggle('is-on', barOn);
        if (barOn && !this.shown.bar) {
          this.shown.bar = true;
          this.track('bar_shown', { surface: 'bar' });
        }

        // what covers the bottom of the viewport, so floating widgets (the Shopify Inbox
        // launcher, pdp-gallery.css) sit above it. offsetHeight, not the rect: the peek may be
        // mid-slide.
        const bottomH = peekOn ? this.peek.offsetHeight : 0;
        if (bottomH !== this.bottomH) {
          this.bottomH = bottomH;
          document.documentElement.style.setProperty('--pdp-bottom-h', `${bottomH}px`);
        }

        const barH = barOn ? this.offsetHeight : 0;
        // anchors in the stack sit below the buy box, where the bar is on by the time a jump
        // lands, so their offset is the bar's height even while it is still off (0 on phones)
        const cover = mobile ? 0 : this.offsetHeight;
        if (cover !== this.cover) {
          this.cover = cover;
          document.documentElement.style.setProperty('--pdp-bar-h', `${cover}px`);
        }

        // the current chapter: the last one whose top has reached what covers the top (the
        // bar, or on the way up the revealed header, which the bar sits under)
        const header = document.querySelector('.section-header');
        const top = Math.max(0, header ? header.getBoundingClientRect().bottom : 0) + barH;
        let current = null;
        this.chapters.forEach((c) => {
          if (c.getBoundingClientRect().top <= top + 8) current = c.id;
        });
        if (current !== this.current) {
          this.current = current;
          this.navs().forEach((nav) =>
            nav.querySelectorAll('a').forEach((a) => {
              const here = current ? a.getAttribute('href') === `#${current}` : a.dataset.chapter === 'overview';
              if (here) a.setAttribute('aria-current', 'true');
              else a.removeAttribute('aria-current');
              // a row too wide for the screen (320) scrolls sideways: keep the current one in view
              if (here && nav.scrollWidth > nav.clientWidth) {
                const left = a.offsetLeft - (nav.clientWidth - a.offsetWidth) / 2;
                nav.scrollTo({ left: Math.max(0, left), behavior: this.reduceMotion() ? 'auto' : 'smooth' });
              }
            })
          );
        }
      }

      sizePending() {
        const state = window.NaomiPDP?.state;
        if (state) return !!state.sizePending;
        return !!this.buyRoot()?.querySelector('.nj-size-dropdown__current--placeholder');
      }

      handleClick(event) {
        const target = event.target;
        const chapter = target.closest?.('[data-pdp-bar-chapters] a');
        if (chapter) {
          this.track('chapter_nav_clicked', { chapter: chapter.dataset.chapter });
          const to = document.getElementById(chapter.getAttribute('href').slice(1));
          if (to) {
            event.preventDefault();
            // Overview: the buy box, its title just under the site header that comes back
            this.jump(to, chapter.dataset.chapter !== 'overview');
          }
          return;
        }
        if (target.closest?.('.pdp-bar__id')) {
          event.preventDefault();
          return this.rise('tap', 'bar');
        }
        const cta = target.closest?.('[data-pdp-bar-cta]');
        if (cta) return this.handleCta(event, this.peek?.contains(cta) ? 'peek' : 'bar');
        if (target.closest?.('[data-pdp-peek-open]')) {
          // the click a finished drag can leave behind
          if (Date.now() - (this.dragged || 0) < 500) return;
          return this.rise('tap');
        }
        if (target.closest?.('[data-pdp-peek-dots]')) return this.jumpTo(event.clientY);
      }

      handleCta(event, surface) {
        if (this.mobile.matches && !this.chosen) {
          // "Select options": the buy box comes up instead of a submit
          event.preventDefault();
          this.track('cta_redirected_to_options', { surface });
          this.rise('select_options', surface);
          return;
        }
        if (surface === 'peek' && this.sizePending()) {
          // "Select a ring size": the buy box comes up instead of a submit
          event.preventDefault();
          this.track('cta_redirected_to_size', { surface });
          this.rise('missing_size');
          return;
        }
        if (this.sizePending()) return; // the submit goes on; size-dropdown.js turns it into a scroll to the size menu and tracks it
        this.track('sticky_atc_clicked', { surface });
      }
    }
  );
}
